import { BackendApiClient } from '../../auth/api/backend-client';
import { BackendValidationError } from '../../../shared/lib/backend-errors';
import { requireList, requireObject } from '../../../shared/lib/response-shape';
import type { ScopeContext } from '../../auth/model/types';

const backendClient = new BackendApiClient();

/**
 * The two parse-request resources the parser exposes, named by their own route path —
 * the fleet's own vocabulary for this pair (`ParseRequestType` / `request_type` in
 * `b-cell-parser-v1`'s `label/parse_request.py`, and the matching `Resource` labels in
 * common-core). `position-parse-request` turns a holdings statement into
 * PositionSnapshots; `trade-parse-request` turns a blotter into ExternalTrades. The
 * request type is the only thing that fixes which operation the rows become — there is
 * no `type` field on the request or on its results any more; the path is the type.
 */
export type ParseRequestType = 'position-parse-request' | 'trade-parse-request';

/**
 * Everything the panel needs to know about one import — one parse-request type — keyed
 * by a `Record` over the whole union rather than per-type `t(`...${requestType}`)`
 * template lookups or hardcoded `=== 'position-parse-request'` branches scattered
 * across the panel and the hook. The two entry buttons that open the panel still live
 * as separate, hardcoded literals in their own tabs (see `PortfolioDetail` and
 * `OperationsTab`) — a third `ParseRequestType` still needs a new button placed by
 * hand, no `Record` fixes that. But everything the panel renders, every path the API
 * client calls, and everything the hook decides once a request type is chosen reads
 * off this one table: a new member fails to compile here instead of silently rendering
 * a panel with no title, no template, posting to a path nobody declared, or a
 * request-level date silently sent (or silently dropped) for a request type nobody
 * taught the check about.
 *
 * `path` is the request type's own route — `POST` creates, `GET` lists, `GET /{id}`
 * polls, `GET /{id}/result` reads the rows. Its type is derived from the key it sits
 * under, so a path pasted under the wrong request type fails to compile too.
 * `templateHref` points at a static asset under `public/templates/`; its bytes are a
 * cross-repo contract with the parser's own test fixtures, so nothing here builds or
 * transforms it, and the file names keep the operation vocabulary (`external-trade`,
 * `position-snapshot`) the rows are named after. `requiresReferenceDate` is what the
 * hook and the panel share for "does this request type ever send/render a request-level
 * `reference_time`" — a holdings file carries no date column of its own at all, so the
 * one `reference_time` dates every row and the completeness declaration alike; a
 * blotter row always carries its own date, so `/trade-parse-request` has no such field
 * and rejects one as an unknown key. `uniqueIdOperation` names the pre-split operation
 * (`ParseOperation` in the parser's `label/parse_request.py`: `position-snapshot` /
 * `external-trade`) each request type composes into `parseRequestUniqueId` below — see
 * that function's own comment for why the value must stay the operation, never the
 * request type. `declaresCompleteness` is whether this request type can declare its file
 * the complete statement of the portfolio at its reference time — the parser accepts
 * `is_complete` on `/position-parse-request` only, and refuses it on
 * `/trade-parse-request` as an unknown key, because a blotter is a list of events and
 * never a statement of what is held. Everything that decides whether the flag is asked
 * for, sent, or composed into the idempotency key reads this one field rather than
 * testing the request type by hand.
 */
export interface ImportTypeConfig {
  path: `/${ParseRequestType}`;
  title: string;
  hint: string;
  templateHref: string;
  templateHint: string;
  requiresReferenceDate: boolean;
  declaresCompleteness: boolean;
  uniqueIdOperation: 'position-snapshot' | 'external-trade';
}

type ImportTypeConfigByRequestType = {
  [Type in ParseRequestType]: ImportTypeConfig & { path: `/${Type}` };
};

export const IMPORT_TYPE_CONFIG: ImportTypeConfigByRequestType = {
  'trade-parse-request': {
    path: '/trade-parse-request',
    title: 'portfolio.import.title.trade-parse-request',
    hint: 'portfolio.import.typeHint.trade-parse-request',
    templateHref: '/templates/veltiz-external-trade-example.csv',
    templateHint: 'portfolio.import.templateHint.trade-parse-request',
    requiresReferenceDate: false,
    declaresCompleteness: false,
    uniqueIdOperation: 'external-trade',
  },
  'position-parse-request': {
    path: '/position-parse-request',
    title: 'portfolio.import.title.position-parse-request',
    hint: 'portfolio.import.typeHint.position-parse-request',
    templateHref: '/templates/veltiz-position-snapshot-example.csv',
    templateHint: 'portfolio.import.templateHint.position-parse-request',
    requiresReferenceDate: true,
    declaresCompleteness: true,
    uniqueIdOperation: 'position-snapshot',
  },
};

/**
 * Every request type, in the table's own order — for a reader that has to cover both,
 * such as the booking marker's running-import count. Derived from `IMPORT_TYPE_CONFIG`
 * rather than typed out so it can never list a request type the table does not know.
 */
export const PARSE_REQUEST_TYPES = Object.keys(IMPORT_TYPE_CONFIG) as ParseRequestType[];

export type ParseRequestStatus =
  | 'created'
  | 'processing'
  | 'success'
  | 'partial'
  | 'rejected'
  | 'failed';

/**
 * The four statuses the pipeline stops at.
 *
 * This is also the gate on reading rows: `GET /{id}/result` refuses a request that has
 * not reached one of these with a 400, because a request that parsed nothing and one
 * that has not parsed *yet* would otherwise read identically as two empty arrays. The
 * result query is enabled off this list for exactly that reason.
 */
export const TERMINAL_PARSE_REQUEST_STATUSES: ParseRequestStatus[] = [
  'success',
  'partial',
  'rejected',
  'failed',
];

export function isTerminalParseStatus(status: ParseRequestStatus | undefined): boolean {
  return status != null && TERMINAL_PARSE_REQUEST_STATUSES.includes(status);
}

/**
 * Every status the parser can answer, as the keys of a `Record` over the whole union —
 * the same shape `IMPORT_TYPE_CONFIG` uses, for the same reason: a seventh status fails
 * to compile here instead of silently landing outside both lists below.
 */
const PARSE_REQUEST_STATUSES = Object.keys({
  created: true,
  processing: true,
  success: true,
  partial: true,
  rejected: true,
  failed: true,
} satisfies Record<ParseRequestStatus, true>) as ParseRequestStatus[];

/**
 * The statuses an import still holds while its rows are being read and recorded — the
 * complement of `TERMINAL_PARSE_REQUEST_STATUSES`, derived rather than typed out so the
 * two can never overlap or leave a gap. The parser's `status` filter takes one value per
 * read and each request type has its own listing, so the booking marker issues one
 * `getParseRequests` call per request type per entry here.
 */
export const ACTIVE_PARSE_REQUEST_STATUSES: ParseRequestStatus[] =
  PARSE_REQUEST_STATUSES.filter((status) => !TERMINAL_PARSE_REQUEST_STATUSES.includes(status));

/** Result metadata, embedded by `include_fields=results`. Never carries row content. */
export interface ParseResultSummary {
  id: string;
  outcome: 'success' | 'rejected';
  row_count: number;
}

/**
 * One request of either type, as the poll and the listings read it. Both request types
 * answer this same shape; a `position-parse-request` also carries the `reference_time`
 * it was created with.
 */
export interface ParseRequest {
  id: string;
  status: ParseRequestStatus;
  attachment_id: string;
  account_id: string;
  portfolio_id: string;
  unique_id?: string | null;
  /** The instant every row in the file was read as of, and — when `is_complete` is
   *  true — the instant every position the file does not list was closed. Only a
   *  `position-parse-request` ever carries it; a blotter row carries its own date
   *  instead. The settled card reads this back for the "applied" line rather than the
   *  form's own date field, so it still names the date this request actually settled
   *  with even after the field has moved on to a later attempt. The wire value is the
   *  explicit UTC instant `composeReferenceTimeUtcMidnight` composes
   *  (`YYYY-MM-DDT00:00:00+00:00`); a reader wants only its first 10 characters, the
   *  calendar day, never a timezone-converted one. Optional so a deployment that sends
   *  no key at all — or a `trade-parse-request`, which never carries one — reads as no
   *  date to show. */
  reference_time?: string;
  /** Whether this request declared its file the complete statement of the portfolio at
   *  `reference_time`. Only a `position-parse-request` ever carries it. The parser flips
   *  it to false on any terminal event that made no declaration, so `true` on a `success`
   *  request is the one reading that means the declaration was actually made — every
   *  other status reads false however the request was created. Optional so a deployment
   *  that sends no key at all reads as "not declared" rather than crashing the panel. */
  is_complete?: boolean;
  created: string;
  results?: ParseResultSummary[];
  /** The latest event's reasons — `[]` when it carries none. Optional so an older
   *  deployment that sends no `reasons` key at all reads as "no reasons", not a crash;
   *  `suggestedRequestTypeFromReasons` below treats `undefined` the same as `[]`. Each
   *  entry is the fleet's standard `{code, message}` shape (`code` itself optional — a
   *  reason meant only to be read, never matched against, may carry none). */
  reasons?: (ParsedRowReason | string)[];
}

/**
 * The reason code a `failed` request's reasons carry when the uploaded file would have
 * mapped under the OTHER request type instead — a positions file sent through "Import
 * trades" comes back with reasons whose `code`s include `no-rows-mapped` and
 * `file-shape-position-snapshot`. The parser names the shape after the operation the
 * rows would become (`position-snapshot`, `external-trade`), not after the request
 * type, so this table is where an operation-named reason resolves to the request type
 * to suggest. Keyed by a `Record` over the whole `ParseRequestType` union, same
 * reasoning as `IMPORT_TYPE_CONFIG` above: a third request type fails to COMPILE here
 * instead of silently leaving the suggestion card unable to ever fire for it.
 *
 * `file-shape-external-trade` is the mirror code and is wired in even though only the
 * snapshot direction can fire today. The parser's probe is symmetric — it asks every
 * other request type's header search — but a snapshot's single mandatory column is
 * quantity, which every trades file already carries, so a trades file uploaded as
 * positions derives a schema and never reaches the failure this suggestion hangs off.
 * The console has no way to know which directions the backend can currently detect,
 * and must not need a change the day a new skill makes the other one reachable.
 */
export const SHAPE_REASON_BY_REQUEST_TYPE: Record<ParseRequestType, string> = {
  'trade-parse-request': 'file-shape-external-trade',
  'position-parse-request': 'file-shape-position-snapshot',
};

/**
 * Pulls the code a `reasons` entry names, tolerating every shape a deployment has ever
 * sent: the current `{code, message}` object (read `.code`, which is itself optional —
 * an entry with no `code` names nothing and falls through to `undefined`), and a bare
 * code string from an older deployment that has not yet moved to `ReasonMessage`
 * objects (read as-is). Neither degraded shape throws; both just fail to match any
 * `SHAPE_REASON_BY_REQUEST_TYPE` value, the same as a reason that genuinely names
 * nothing.
 */
function codeFromReason(reason: ParsedRowReason | string): string | undefined {
  return typeof reason === 'string' ? reason : reason.code;
}

/**
 * The other request type a `failed` request's `reasons` name, or `null` when they name
 * none — including when a deployment sends no `reasons` key at all (`undefined` reads
 * the same as `[]`). Built from `SHAPE_REASON_BY_REQUEST_TYPE` so the lookup stays
 * symmetric across both directions instead of a hand-rolled pair of `includes` checks
 * that could drift out of step with each other. Accepts a bare-string entry alongside
 * the current `{code, message}` shape — see `codeFromReason` — so a request read from
 * an older deployment degrades to "no suggestion" instead of crashing the panel.
 * `Array.isArray` guards against a degraded 200 whose `reasons` is truthy but not an
 * array at all (an object, a string) — `?? []` alone only catches `null`/`undefined`
 * and would otherwise call `.map` on that non-array value and throw inside this hook's
 * render body. Neither degraded shape throws.
 */
export function suggestedRequestTypeFromReasons(
  reasons: (ParsedRowReason | string)[] | undefined,
): ParseRequestType | null {
  const reasonCodes = new Set(
    (Array.isArray(reasons) ? reasons : [])
      .map(codeFromReason)
      .filter((code): code is string => code != null),
  );
  const namedRequestType = PARSE_REQUEST_TYPES.find(
    (candidate) => reasonCodes.has(SHAPE_REASON_BY_REQUEST_TYPE[candidate]),
  );
  return namedRequestType ?? null;
}

/**
 * The reason code a terminal request carries when it was created with the completeness
 * flag but made no declaration — some row was refused, or the request failed before any
 * row was read. The parser flips `is_complete` to false on that same event, so this code
 * is what explains the flip rather than leaving the flag silently contradicting the
 * checkbox the user ticked.
 */
export const COMPLETENESS_NOT_APPLIED_REASON = 'completeness-not-applied';

/**
 * The backend's own localized sentence for `completeness-not-applied`, or `null` when the
 * reasons do not carry that code. Rendered as it comes: unlike the shape-mismatch pair in
 * `language.tsx`, the console has no extra call to action to add here, so a hand-written
 * copy would only be a second version of a sentence the parser already maintains in both
 * languages.
 *
 * Tolerates every degraded shape `suggestedRequestTypeFromReasons` does, for the same
 * reason: `undefined` and a non-array `reasons` read as "none", and a bare-string entry
 * from a deployment that predates `ReasonMessage` objects carries no message to render,
 * so it answers `null` instead of throwing inside the panel's render body.
 */
export function completenessNotAppliedMessage(
  reasons: (ParsedRowReason | string)[] | undefined,
): string | null {
  const named = (Array.isArray(reasons) ? reasons : []).find(
    (reason) => codeFromReason(reason) === COMPLETENESS_NOT_APPLIED_REASON,
  );
  if (named == null || typeof named === 'string') return null;
  return named.message || null;
}

export interface ParsedRowReason {
  code?: string;
  message: string;
  parameter?: string;
}

export interface ParseRequestResponse {
  requests: ParseRequest[];
  cursor?: string;
}

export interface SuccessRow {
  row_number: number;
  raw: Record<string, unknown>;
  trade_id?: string;
  snapshot_id?: string;
}

export interface RejectedRow {
  row_number: number;
  raw: Record<string, unknown>;
  reasons: ParsedRowReason[];
}

export interface ParseResult {
  success_rows: SuccessRow[];
  rejected_rows: RejectedRow[];
}

/**
 * One request to create, for either type. The request type itself is not a field: it
 * is the path the batch is posted to, and a `type` key in the body is refused as
 * unknown.
 */
export interface CreateParseRequest {
  attachment_id: string;
  portfolio_id: string;
  unique_id?: string;
  /** The position date for snapshot rows in files that carry no date column of their
   *  own. Sent only to `/position-parse-request` — a blotter row always carries its own
   *  date, so `/trade-parse-request` has no such field and refuses it as an unknown key.
   *  An explicit UTC instant (`YYYY-MM-DDT00:00:00+00:00`, composed by
   *  `composeReferenceTimeUtcMidnight`), matching the parser's own default of 00:00 UTC
   *  on the day the request is created — not the bare-date/BRT-midnight reading
   *  `common_core.validator.datetime.Date` uses elsewhere. Brazil's UTC-3 offset means
   *  that reading still lands on the same UTC calendar day (03:00 UTC), so the explicit
   *  form is not routing around a day shift; it exists only to match the parser's own
   *  literal wire example. */
  reference_time?: string;
  /** Declares the file the complete statement of the portfolio at `reference_time`:
   *  every position the file does not list is closed as of that instant. Sent only to a
   *  request type whose config declares it (`declaresCompleteness`) — a blotter declares
   *  nothing and `/trade-parse-request` refuses the key as unknown. The parser makes it
   *  mandatory on the holdings route, so a declaring dispatch always sends it, `false`
   *  included, and never omits it. */
  is_complete?: boolean;
}

/**
 * The deterministic idempotency key for parsing one attachment as one request type —
 * the same `attachment/{attachment_id}/type/{type}` composition the mailbox supplies
 * (parser README → Idempotency), so a client-side double dispatch collapses into one
 * server-refused repeat instead of booking the rows twice.
 *
 * The `type` segment holds the OPERATION the request type composes into
 * (`uniqueIdOperation` on `IMPORT_TYPE_CONFIG`: `position-snapshot` /
 * `external-trade`), never the request type itself. Before the request types split
 * into two Kinds, one shared Kind held both operations, so the operation was the only
 * thing that told two requests over the same attachment apart, and every
 * already-stored key still carries that value. The split moved that disambiguation to
 * the server-side namespace instead (each request type is now its own Kind), which
 * makes the segment redundant — but changing its VALUE, not only its meaning, would
 * mint a different string for a file already imported before this split, so a
 * re-upload would no longer match its own earlier key and would book the same rows a
 * second time. Composing from `uniqueIdOperation` keeps the byte value identical to
 * what a pre-split caller already sent, matching the parser README's own "no client
 * key changes shape" guarantee. A refused repeat is recovered with
 * `findParseRequestByUniqueId` below.
 *
 * A completeness declaration is a different import of the same file, so it needs a key of
 * its own: the same rows, but also every position the file does not list closed at the
 * reference time. The `/complete` segment is appended ONLY when the flag is true, leaving
 * the not-complete key byte-identical to what every holdings import sent before this
 * console could declare anything. Appending the flag to both spellings would instead mint
 * a fresh key for every file already imported, and a re-upload of one would no longer
 * match its own earlier key — booking the same rows a second time.
 */
export function parseRequestUniqueId(
  attachmentId: string,
  requestType: ParseRequestType,
  isComplete: boolean,
): string {
  const config = IMPORT_TYPE_CONFIG[requestType];
  const key = `attachment/${attachmentId}/type/${config.uniqueIdOperation}`;
  // Read off the config rather than trusting the caller's flag alone: a request type that
  // declares nothing must compose the same key whatever the panel's checkbox last held,
  // so a switch from a checked holdings import to a trades one cannot mint a `/complete`
  // key for a route that refuses the field.
  return config.declaresCompleteness && isComplete ? `${key}/complete` : key;
}

/**
 * Creates parse requests of one request type. Batch-shaped even for the one request
 * the console sends, per the house POST rule.
 *
 * Both synchronous preconditions land here as a 400: `invalid-attachment` when the
 * attachment has not cleared its scan, `content-type-unsupported` when the sniffed
 * type is not parsable. The caller surfaces the backend's own message rather than
 * restating either.
 */
export async function createParseRequests(
  sessionId: string,
  scopeContext: ScopeContext,
  requestType: ParseRequestType,
  requests: CreateParseRequest[],
): Promise<ParseRequest[]> {
  const response = await backendClient.post(
    IMPORT_TYPE_CONFIG[requestType].path, sessionId, { requests }, scopeContext,
  );
  if (response.success && response.data) {
    return requireList<ParseRequest>(
      response.data as object, 'requests', 'Unreadable parse request response',
    );
  }
  if (response.validationError) throw response.validationError;
  throw new BackendValidationError(response.error || 'Failed to start parsing the file');
}

/**
 * The parse request a previous POST to this request type recorded under this key, or
 * `null` if none did.
 *
 * The recovery half of the deterministic key: a dispatch refused `already-exists`
 * means the request already exists server-side — created by a timed-out earlier
 * attempt, or by a concurrent duplicate — and this read is how the caller adopts it
 * rather than reporting a failure for work that is in fact running. The key is
 * namespaced per request type on the server, so the read has to name the same request
 * type the refused POST went to.
 */
export async function findParseRequestByUniqueId(
  sessionId: string,
  scopeContext: ScopeContext,
  requestType: ParseRequestType,
  uniqueId: string,
): Promise<ParseRequest | null> {
  const response = await backendClient.get(
    `${IMPORT_TYPE_CONFIG[requestType].path}?unique_id=${encodeURIComponent(uniqueId)}`,
    sessionId, scopeContext,
  );
  if (response.success && response.data) {
    const requests = requireList<ParseRequest>(
      response.data as object, 'requests', 'Unreadable parse request response',
    );
    return requests[0] ?? null;
  }
  if (response.validationError) throw response.validationError;
  throw new BackendValidationError(response.error || 'Failed to read the parse request');
}

/**
 * Every parse request of one request type currently at one `status`, optionally
 * narrowed to one portfolio. The parser's status filter is single-valued and each
 * request type has its own listing, so a caller wanting several statuses across both
 * request types issues one read per request type per status; `portfolio_id` is a
 * server-side filter (the parser's own validator accepts it), so a caller wanting one
 * portfolio's requests never has to read the account-wide page and narrow it by hand.
 * A `cursor` on the response means there are more.
 */
export async function getParseRequests(
  sessionId: string,
  scopeContext: ScopeContext,
  requestType: ParseRequestType,
  query: { status: ParseRequestStatus; portfolioId?: string },
): Promise<ParseRequestResponse> {
  const params = new URLSearchParams();
  params.set('status', query.status);
  if (query.portfolioId) params.set('portfolio_id', query.portfolioId);
  const response = await backendClient.get(
    `${IMPORT_TYPE_CONFIG[requestType].path}?${params.toString()}`, sessionId, scopeContext,
  );
  if (response.success && response.data) {
    const body = response.data as ParseRequestResponse;
    return {
      ...body,
      requests: requireList<ParseRequest>(
        body, 'requests', 'Unreadable parse request response',
      ),
    };
  }
  if (response.validationError) throw response.validationError;
  throw new BackendValidationError(response.error || 'Failed to list the parse requests');
}

/**
 * One parse request. `include_fields=results` embeds each result's `row_count` so the
 * progress view can report how many rows landed on each side without reading either
 * file — naming an unsupported field embeds nothing rather than failing, so asking is
 * safe against an older deployment.
 */
export async function getParseRequestById(
  sessionId: string,
  scopeContext: ScopeContext,
  requestType: ParseRequestType,
  requestId: string,
): Promise<ParseRequest> {
  const response = await backendClient.get(
    `${IMPORT_TYPE_CONFIG[requestType].path}/${requestId}?include_fields=results`,
    sessionId, scopeContext,
  );
  if (response.success && response.data) {
    return requireObject<ParseRequest>(
      response.data as object, 'request', 'Unreadable parse request response',
    );
  }
  if (response.validationError) throw response.validationError;
  throw new BackendValidationError(response.error || 'Failed to read the parse request');
}

/**
 * The request's rows, split by outcome.
 *
 * Only call this once the request is terminal — see
 * `TERMINAL_PARSE_REQUEST_STATUSES`. An outcome with no rows answers an empty array;
 * it is never absent, so both keys are required rather than defaulted.
 */
export async function getParseResult(
  sessionId: string,
  scopeContext: ScopeContext,
  requestType: ParseRequestType,
  requestId: string,
): Promise<ParseResult> {
  const response = await backendClient.get(
    `${IMPORT_TYPE_CONFIG[requestType].path}/${requestId}/result`, sessionId, scopeContext,
  );
  if (response.success && response.data) {
    const result = requireObject<Record<string, unknown>>(
      response.data as object, 'result', 'Unreadable parse result response',
    );
    return {
      success_rows: requireList<SuccessRow>(result, 'success_rows', 'Unreadable parse result response'),
      rejected_rows: requireList<RejectedRow>(result, 'rejected_rows', 'Unreadable parse result response'),
    };
  }
  if (response.validationError) throw response.validationError;
  throw new BackendValidationError(response.error || 'Failed to read the parse results');
}
